import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import YouTube from 'react-youtube';
import { ArrowLeft, PlayCircle, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import { useCategory, useRecordActivity, useActivityTimer,  useHierarchicalProgress, useUserActivities } from '../../hooks';
import { Card, LoadingSpinner, Button, Badge, Alert } from '../../components/common';

import QuickUserActions from '@/components/users/QuickUserActions';

const VideoPage: React.FC = () => {
  const { categoryId } = useParams<{ categoryId: string }>();
  const { data: categoryData, isLoading } = useCategory(categoryId!);
  const recordActivity = useRecordActivity();
  const { elapsedTime, getElapsedSeconds } = useActivityTimer();
  
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videosWatched, setVideosWatched] = useState<Set<string>>(new Set());
  const [playerReady, setPlayerReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const category = categoryData?.category;
  const videos = category?.videos || [];

  const currentVideo = videos[currentVideoIndex];

  const { data: activitiesData } = useUserActivities({ categoryId: categoryId! });
    
      const progress = useHierarchicalProgress(
        categoryData?.category?.subcategories || [],
        activitiesData?.users || []
      );

  // Registrar actividad cuando se completa un video
  const handleVideoEnd = async () => {
    if (!currentVideo) return;

    const newWatchedVideos = new Set(videosWatched);
    newWatchedVideos.add(currentVideo.id);
    setVideosWatched(newWatchedVideos);

    // Registrar actividad
    await recordActivity.mutateAsync({
      categoryId: categoryId!,
      activityType: 'video',
      completed: newWatchedVideos.size === videos.length,
      timeSpent: getElapsedSeconds(),
      metadata: {
        videoId: currentVideo.id,
        videoTitle: currentVideo.title,
        watchedVideos: Array.from(newWatchedVideos)
      }
    });

    setIsPlaying(false);
  };

  // Configuración del reproductor de YouTube
  const youtubeOptions = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 0,
      controls: 1,
      rel: 0,
      showinfo: 0,
      modestbranding: 1,
    },
  };

  const onPlayerReady = (event: any) => {
    setPlayerReady(true);
  };

  const onPlayerStateChange = (event: any) => {
    // 0 = ended, 1 = playing, 2 = paused
    if (event.data === 0) {
      handleVideoEnd();
    } else if (event.data === 1) {
      setIsPlaying(true);
    } else if (event.data === 2) {
      setIsPlaying(false);
    }
  };

  const extractVideoId = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : '';
  };

  const nextVideo = () => {
    if (currentVideoIndex < videos.length - 1) {
      setCurrentVideoIndex(currentVideoIndex + 1);
      setIsPlaying(false);
    }
  };

  const prevVideo = () => {
    if (currentVideoIndex > 0) {
      setCurrentVideoIndex(currentVideoIndex - 1);
      setIsPlaying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!category || videos.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-brinsa-azulOscuro/50 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-brinsa-azul mb-4">
          No hay videos disponibles
        </h2>
        <p className="text-brinsa-azulOscuro/80 mb-6">
          Esta categoría no tiene videos configurados aún.
        </p>
        <Link to={`/category/${categoryId}`}>
          <Button variant="outline">
            Volver a la categoría
          </Button>
        </Link>
      </div>
    );
  }

  const completedVideos = videosWatched.size;
  const progressPercentage = (completedVideos / videos.length) * 100;

  return (
    <div className="space-y-6">
      {/* Navegación */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to={`/category/${categoryId}`}
          className="inline-flex items-center text-brinsa-azulOscuro/80 hover:text-brinsa-azul transition-colors"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Volver a {category.name}
        </Link>
      </motion.div>

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-brinsa-azul mb-4">
            Videos: {category.name}
          </h1>
          <p className="text-lg text-brinsa-azulOscuro/80">
            Aprende sobre nuestros productos a través de contenido audiovisual
          </p>
        </div>
      </motion.div>

      {/* Progreso */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
      >
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-brinsa-azulOscuro/80">
                Progreso de videos
              </span>
              <span className="text-sm text-brinsa-azulOscuro/80">
                {completedVideos}/{videos.length} completados
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-2">
              <div
                className="bg-brinsa-azulClaro h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-2 text-sm text-brinsa-azulOscuro/70">
              <span>Tiempo transcurrido: {Math.floor(elapsedTime / 60)}:{(elapsedTime % 60).toString().padStart(2, '0')}</span>
              {completedVideos === videos.length && (
                <Badge variant="success" size="sm">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  ¡Completado!
                </Badge>
              )}
            </div>
          </div>
        </Card>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Reproductor principal */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="lg:col-span-3"
        >
          <Card>
            <div className="aspect-video bg-black rounded-t-lg overflow-hidden">
              {currentVideo && (
                <YouTube
                  videoId={extractVideoId(currentVideo.youtubeUrl)}
                  opts={youtubeOptions}
                  onReady={onPlayerReady}
                  onStateChange={onPlayerStateChange}
                  className="w-full h-full"
                />
              )}
            </div>
            
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-brinsa-azul mb-2">
                    {currentVideo?.title}
                  </h2>
                  {currentVideo?.description && (
                    <p className="text-brinsa-azulOscuro/85">
                      {currentVideo.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {videosWatched.has(currentVideo?.id || '') && (
                    <Badge variant="success" size="sm">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Visto
                    </Badge>
                  )}
                  {isPlaying && (
                    <Badge variant="info" size="sm">
                      Reproduciendo
                    </Badge>
                  )}
                </div>
              </div>

              {/* Controles de navegación */}
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  onClick={prevVideo}
                  disabled={currentVideoIndex === 0}
                >
                  Video anterior
                </Button>
                
                <span className="text-sm text-brinsa-azulOscuro/70">
                  {currentVideoIndex + 1} de {videos.length}
                </span>
                
                <Button
                  onClick={nextVideo}
                  disabled={currentVideoIndex === videos.length - 1}
                >
                  Siguiente video
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Lista de videos */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="lg:col-span-1"
        >
          <Card title="Lista de Videos">
            <div className="space-y-3">
              {videos.map((video, index) => (
                <div
                  key={video.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    index === currentVideoIndex
                      ? 'border-brinsa-celeste bg-brinsa-celeste/10'
                      : 'border-slate-200 hover:border-brinsa-celeste/40'
                  }`}
                  onClick={() => setCurrentVideoIndex(index)}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          videosWatched.has(video.id)
                            ? 'bg-brinsa-verde/15 text-brinsa-verde'
                            : index === currentVideoIndex
                            ? 'bg-brinsa-azulClaro/15 text-brinsa-azul'
                            : 'bg-slate-100 text-brinsa-azulOscuro'
                        }`}
                      >
                        {videosWatched.has(video.id) ? (
                          <CheckCircle className="w-4 h-4" />
                        ) : (
                          <PlayCircle className="w-4 h-4" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-brinsa-azul truncate">
                        {video.title}
                      </h4>
                      {video.duration && (
                        <div className="flex items-center mt-1 text-xs text-brinsa-azulOscuro/70">
                          <Clock className="w-3 h-3 mr-1" />
                          {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>

      {/* Mensaje de completado */}
      {completedVideos === videos.length && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <Alert
            type="success"
            title="¡Excelente trabajo!"
            message="Has completado todos los videos de esta categoría. Continúa con las otras actividades para obtener tu certificado."
          />
        </motion.div>
      )}
      <QuickUserActions progress = {progress}/>
    </div>
  );
};

export default VideoPage;
